import { FileText, Folder, X } from "lucide-react";
import type { DragEvent } from "react";
import { useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface DocumentUploadProps {
  onFileSelect: (file: File) => void;
  selectedFile: File | null;
  disabled?: boolean;
  className?: string;
  variant?: "default" | "glass";
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
const ACCEPTED_MIME_TYPE = "application/pdf";

export default function DocumentUpload({
  onFileSelect,
  selectedFile,
  disabled = false,
  className,
  variant = "default",
}: DocumentUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    if (file.type !== ACCEPTED_MIME_TYPE) {
      return "Please upload a PDF file";
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File size exceeds 10MB limit (${(file.size / 1024 / 1024).toFixed(2)}MB)`;
    }
    return null;
  };

  const handleFile = (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    onFileSelect(file);
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const handleRemoveFile = () => {
    setError("");
    onFileSelect(null as any);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };

  const isGlass = variant === "glass";

  return (
    <div className={cn("w-full", className)}>
      <div
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          "relative border border-dashed p-8 text-center cursor-pointer transition-all duration-200 flex flex-col items-center justify-center min-h-[200px]",
          isGlass
            ? [
                "rounded-xl border-content-muted/50 bg-content-subtle/60",
                isDragging
                  ? "bg-brand/5 border-brand"
                  : "hover:bg-content hover:border-content-muted",
              ]
            : [
                isDragging
                  ? "border-gray-900 bg-gray-100"
                  : "border-gray-300 bg-white hover:border-gray-400",
              ],
          disabled && "opacity-50 cursor-not-allowed"
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf"
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled}
        />

        {selectedFile ? (
          <div className="flex flex-col items-center gap-4">
            <div
              className={cn("p-4", isGlass ? "bg-brand/10 border border-brand/20" : "bg-gray-100")}
            >
              <FileText
                className={cn("w-10 h-10", isGlass ? "text-brand-foreground" : "text-gray-600")}
              />
            </div>
            <div className="flex flex-col items-center gap-1">
              <p
                className={cn(
                  "text-lg font-medium",
                  isGlass ? "text-content-foreground" : "text-gray-900"
                )}
              >
                {selectedFile.name}
              </p>
              <p className={cn("text-sm", isGlass ? "text-content-muted" : "text-gray-500")}>
                {formatFileSize(selectedFile.size)}
              </p>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveFile();
              }}
              className={cn(
                "mt-2 px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 uppercase tracking-wider",
                isGlass
                  ? "border border-content-border text-content-muted hover:bg-content hover:text-content-foreground"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-900"
              )}
            >
              <X className="w-4 h-4" /> Remove File
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4">
            <div className={cn(isGlass ? "text-content-muted" : "text-gray-400")}>
              <Folder className="w-10 h-10" strokeWidth={1.5} />
            </div>
            <div className="space-y-1">
              <p
                className={cn(
                  "text-[17px] font-bold",
                  isGlass ? "text-content-foreground" : "text-gray-900"
                )}
              >
                Drag and drop your file here
              </p>
              <p className={cn("text-sm", isGlass ? "text-content-muted" : "text-gray-500")}>
                <span className="font-semibold text-[#1E6F4F] underline">Click to upload</span> or
                drag and drop
              </p>
            </div>
            {!isGlass && (
              <p className="text-xs uppercase tracking-wider text-gray-500">
                PDF files only (max 10MB)
              </p>
            )}
          </div>
        )}
      </div>

      {error && (
        <p className="mt-3 text-sm text-red-700 text-center bg-red-50 border border-red-200 py-1 px-3 inline-block w-full">
          {error}
        </p>
      )}
    </div>
  );
}
